import { useState, useCallback, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { useAbortController } from './useAbortController';
import { useTableSearch } from './table/useTableSearch';


export interface PaginatedListSort {
  key: string;
  direction: 'ASC' | 'DESC';
}

/**
 * Parameters passed to the domain-level `fetchFn` on every fetch.
 */
export interface PaginatedFetchParams<TFilters> {
  page: number;
  pageSize: number;
  searchText: string | undefined;
  sort: PaginatedListSort | undefined;
  filters: TFilters;
}

/**
 * Normalised result that `fetchFn` must return on success.
 */
export interface PaginatedFetchResult<TRow> {
  items: TRow[];
  totalRecords: number;
}

export interface UsePaginatedListConfig<TRow, TFilters> {
  /**
   * Async function that calls the service and returns normalised data.
   * - On success: return `{ items, totalRecords }`.
   * - On error: call `showApiError` (or equivalent), then return `null`.
   *
   * **Must be wrapped in `useCallback`** to avoid infinite re-fetch loops,
   * because it is used as a dependency of the internal fetch effect.
   */
  fetchFn: (
    params: PaginatedFetchParams<TFilters>,
    signal: AbortSignal,
  ) => Promise<PaginatedFetchResult<TRow> | null>;

  /** Initial filter state. */
  initialFilters: TFilters;

  /** Initial sort applied on mount. */
  initialSort?: PaginatedListSort;

  /** Rows per page on mount. @default 10 */
  initialPageSize?: number;

  /**
   * Maps Table column keys to backend field names for server-side sorting.
   * An inline object literal is safe here — the hook stabilises it via a ref.
   */
  orderByMap?: Record<string, string>;

  /**
   * When `true`, deduplicates rows by their `id` field after each fetch.
   * Requires that `TRow` has an `id: number | string` property.
   * @default false
   */
  deduplicate?: boolean;

  /**
   * When `false`, the fetch is skipped without changing the loading state.
   * Useful for permission gates: `enabled: isLoaded && canView`.
   * The fetch is automatically triggered when this switches to `true`.
   * @default true
   */
  enabled?: boolean;
}

export interface UsePaginatedListResult<TRow, TFilters> {
  rows: TRow[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  /** Normalises the value and resets page to 1. */
  setPageSize: (size: number) => void;
  loading: boolean;
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  /** Current sort state (useful when domain hooks need to pass sort to export functions). */
  sort: PaginatedListSort | undefined;
  filters: TFilters;
  /** Updates filters and automatically resets page to 1 when the value changes. */
  setFilters: Dispatch<SetStateAction<TFilters>>;
  /** Current debounced search text (useful when the domain hook needs to include it in exports). */
  debouncedSearchText: string;
  /** Forces the debounced search to resolve immediately (e.g. on Enter key). */
  flushSearch: () => void;
  /** Manually triggers a re-fetch with the current parameters. */
  refetch: () => void;
}

/**
 * Generic hook that encapsulates all pagination boilerplate for list pages.
 *
 * Handles: pagination state, debounced search, server-side sort, filter state,
 * abort-signal staleness detection, page-overflow guard, and optional dedup.
 *
 * Domain hooks stay thin: they provide a `fetchFn`, columns, actions, and any
 * domain-specific state (e.g. totals, catalogs, permission checks).
 *
 * @example
 * ```ts
 * const fetchFn = useCallback(async (params, signal) => {
 *   const response = await myService.list({ ...params.filters, ...params }, signal);
 *   if (!response.success || !response.data) {
 *     showApiError(response, translate('domain:messages.loadError'));
 *     return null;
 *   }
 *   return { items: response.data.items, totalRecords: response.data.totalRecords };
 * }, [showApiError, translate]);
 *
 * const { rows, total, page, setPage, loading, onSearch, onSortChange, filters, setFilters } =
 *   usePaginatedList({ fetchFn, initialFilters: { status: 'all' }, enabled: isLoaded });
 * ```
 */
export function usePaginatedList<TRow, TFilters>({
  fetchFn,
  initialFilters,
  initialSort,
  initialPageSize = 10,
  orderByMap,
  deduplicate = false,
  enabled = true,
}: UsePaginatedListConfig<TRow, TFilters>): UsePaginatedListResult<TRow, TFilters> {
  const [page, internalSetPage] = useState(1);
  const [pageSize, internalSetPageSize] = useState(initialPageSize);
  const [rows, setRows] = useState<TRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState<PaginatedListSort | undefined>(() => initialSort);
  const [filters, internalSetFilters] = useState<TFilters>(() => initialFilters);

  const { createSignal } = useAbortController();
  const { debouncedSearchText, setSearchText, flushSearch } = useTableSearch({
    onDebouncedChange: () => internalSetPage(1),
  });

  // Stabilise orderByMap via a ref so callers can pass an inline object literal
  // without needing useMemo, and without triggering unnecessary effect re-runs.
  const orderByMapRef = useRef<Record<string, string>>(orderByMap ?? {});
  useEffect(() => {
    orderByMapRef.current = orderByMap ?? {};
  }, [orderByMap]);

  const setPage = internalSetPage;

  const setPageSize = useCallback(
    (size: number) => {
      internalSetPageSize(Number(size) || initialPageSize);
      internalSetPage(1);
    },
    [initialPageSize],
  );

  const setFilters = useCallback(
    (updater: SetStateAction<TFilters>) => {
      internalSetFilters((previous) => {
        const next =
          typeof updater === 'function'
            ? (updater as (prev: TFilters) => TFilters)(previous)
            : updater;
        if (next !== previous) {
          internalSetPage(1);
          internalSetPageSize(initialPageSize);
        }
        return next;
      });
    },
    [initialPageSize],
  ) as Dispatch<SetStateAction<TFilters>>;

  const executeFetch = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    const signal = createSignal();

    let result: PaginatedFetchResult<TRow> | null;
    try {
      result = await fetchFn(
        { page, pageSize, searchText: debouncedSearchText || undefined, sort, filters },
        signal,
      );
    } catch {
      if (!signal.aborted) {
        setRows([]);
        setTotal(0);
        setLoading(false);
      }
      return;
    }

    // Stale request — a newer fetch has already started; discard this result.
    if (signal.aborted) return;

    if (result === null) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    let { items } = result;
    if (deduplicate) {
      const seen = new Set<number | string>();
      items = items.filter((item) => {
        const id = (item as Record<string, unknown>)['id'] as number | string;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    }

    setRows(items);
    setTotal(result.totalRecords);
    setLoading(false);
  }, [enabled, page, pageSize, debouncedSearchText, sort, filters, fetchFn, createSignal,
    deduplicate]);

  useEffect(() => {
    void executeFetch();
  }, [executeFetch]);

  // Page-overflow guard: resets to page 1 when total shrinks below current page.
  useEffect(() => {
    if (total > 0) {
      const totalPages = Math.ceil(total / pageSize);
      if (page > totalPages) internalSetPage(1);
    }
  }, [total, page, pageSize]);

  const onSearch = useCallback(
    (text: string) => {
      setSearchText(text);
    },
    [setSearchText],
  );

  // Uses a ref for orderByMap — stable callback with no stale-closure risk.
  const onSortChange = useCallback(
    (columnKey: string, direction: 'asc' | 'desc') => {
      setSort({
        key: orderByMapRef.current[columnKey] ?? columnKey,
        direction: direction.toUpperCase() as 'ASC' | 'DESC',
      });
      internalSetPage(1);
    },
    [],
  );

  const refetch = useCallback(() => {
    void executeFetch();
  }, [executeFetch]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    sort,
    filters,
    setFilters,
    debouncedSearchText,
    flushSearch,
    refetch,
  };
}
